/**
 * NotificationToggle — single button to enable/disable web push.
 *
 * Behaviour
 * ─────────
 *   • Hidden when the browser doesn't support Push (covers iOS Safari out of PWA).
 *   • Shows current permission state with a colour-coded icon.
 *   • On enable: requests permission + registers subscription with backend.
 *   • On disable: pops local subscription + tells backend.
 */
import { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  currentPermission, getActiveSubscription, isSupported,
  subscribe, unsubscribe,
} from '@/utils/push';

const NotificationToggle = ({ compact = false }) => {
  const { getAuthHeaders } = useAuth();
  const [permission, setPermission] = useState('default');
  const [hasSub, setHasSub] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPermission(currentPermission());
    getActiveSubscription().then((s) => setHasSub(!!s)).catch(() => null);
  }, []);

  if (!isSupported()) return null;

  const enabled = permission === 'granted' && hasSub;

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        await unsubscribe(getAuthHeaders);
        setHasSub(false);
        toast.success('Notificaciones desactivadas');
      } else {
        await subscribe(getAuthHeaders);
        setHasSub(true);
        setPermission('granted');
        toast.success('Notificaciones activadas — recibirás alertas en este dispositivo');
      }
    } catch (e) {
      if (e?.code === 'denied' || e?.message === 'permission-denied') {
        toast.error('Permiso denegado. Activá las notificaciones desde la configuración del navegador.');
      } else {
        toast.error('No se pudo activar push: ' + (e?.message || 'error'));
      }
    } finally {
      setBusy(false);
    }
  };

  const Icon = enabled ? Bell : BellOff;
  const tone = enabled ? 'text-emerald-500' : 'text-slate-400';

  if (compact) {
    return (
      <button
        onClick={onClick}
        disabled={busy}
        className={`p-2 rounded-lg hover:bg-slate-800 transition-colors ${tone}`}
        title={enabled ? 'Notificaciones activadas' : 'Activar notificaciones'}
        data-testid="notification-toggle"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs bg-slate-800/60 hover:bg-slate-700 text-slate-200 transition-colors"
      data-testid="notification-toggle"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className={`w-4 h-4 ${tone}`} />}
      <span className="flex-1 text-left truncate">
        {enabled ? 'Notificaciones activadas' : 'Activar notificaciones'}
      </span>
    </button>
  );
};

export default NotificationToggle;
